import { Collapse, Form, Input, InputNumber, Switch, Alert } from '@arco-design/web-react'
import JsonField from './JsonField'
import SensitiveInput from './SensitiveInput'
import SensitiveTextArea from './SensitiveTextArea'
import CopyableLine from './CopyableLine'
import type { OptionsAPI } from './useOptions'
import { FIXED_PUBLIC_SITE_ORIGIN } from '@/utils/commercialSite'
import PaymentChannelCard from './payment/PaymentChannelCard'
import PaymentComplianceCard from './payment/PaymentComplianceCard'
import AmountOptionsEditor, { validateAmountOptions } from './payment/AmountOptionsEditor'
import AmountDiscountEditor, { validateAmountDiscount } from './payment/AmountDiscountEditor'
import PayMethodsEditor, { validatePayMethods } from './payment/PayMethodsEditor'
import type { PaymentChannelStatus } from './payment/PaymentStatusPill'

function verifyJson(text: string, kind: 'object' | 'array' | 'any'): boolean {
  if (!text.trim()) return true
  try {
    const parsed = JSON.parse(text)
    if (kind === 'array' && !Array.isArray(parsed)) return false
    if (
      kind === 'object' &&
      (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null)
    ) {
      return false
    }
    return true
  } catch {
    return false
  }
}

const GENERAL_KEYS = [
  'CustomCallbackAddress',
  'Price',
  'MinTopUp',
  'TopupGroupRatio',
  'PayMethods',
  'payment_setting.amount_options',
  'payment_setting.amount_discount',
]

const EPAY_KEYS = ['PayAddress', 'EpayId', 'EpayKey']

const STRIPE_KEYS = [
  'StripeApiSecret',
  'StripeWebhookSecret',
  'StripePriceId',
  'StripeUnitPrice',
  'StripeMinTopUp',
  'StripePromotionCodesEnabled',
]

const CREEM_KEYS = ['CreemApiKey', 'CreemWebhookSecret', 'CreemProducts', 'CreemTestMode']

const WAFFO_KEYS = [
  'WaffoEnabled',
  'WaffoApiKey',
  'WaffoPrivateKey',
  'WaffoPublicCert',
  'WaffoSandboxApiKey',
  'WaffoSandboxPrivateKey',
  'WaffoSandboxPublicCert',
  'WaffoSandbox',
  'WaffoMerchantId',
  'WaffoCurrency',
  'WaffoUnitPrice',
  'WaffoMinTopUp',
  'WaffoNotifyUrl',
  'WaffoReturnUrl',
  'WaffoSubscriptionReturnUrl',
  'WaffoPayMethods',
]

function epayStatus(get: OptionsAPI['get']): PaymentChannelStatus {
  const addr = get('PayAddress').trim()
  const id = get('EpayId').trim()
  const hasKey = get('EpayKey').trim().length > 0
  if (!addr && !id && !hasKey) return 'disabled'
  if (addr && id && hasKey) return 'ready'
  return 'partial'
}

function stripeStatus(get: OptionsAPI['get']): PaymentChannelStatus {
  const secret = get('StripeApiSecret').trim()
  const webhook = get('StripeWebhookSecret').trim()
  const priceId = get('StripePriceId').trim()
  if (!secret && !webhook && !priceId) return 'disabled'
  if (secret && webhook && priceId) return 'ready'
  return 'partial'
}

function creemStatus(get: OptionsAPI['get']): PaymentChannelStatus {
  const key = get('CreemApiKey').trim()
  const webhook = get('CreemWebhookSecret').trim()
  const products = get('CreemProducts', '[]').trim()
  if (!key && !webhook && products === '[]') return 'disabled'
  if (key && webhook && products !== '[]' && products !== '') return 'ready'
  return 'partial'
}

function waffoStatus(get: OptionsAPI['get'], getBool: OptionsAPI['getBool']): PaymentChannelStatus {
  if (!getBool('WaffoEnabled')) return 'disabled'
  const merchant = get('WaffoMerchantId').trim()
  const apiKey = get('WaffoApiKey').trim()
  const privateKey = get('WaffoPrivateKey').trim()
  if (merchant && (apiKey || privateKey)) return 'ready'
  return 'partial'
}

export default function PaymentSetting({ options }: { options: OptionsAPI }) {
  const { get, getBool, getNumber, patch, save, saving, reload } = options
  const callbackBase = (
    get('CustomCallbackAddress') || get('ServerAddress', FIXED_PUBLIC_SITE_ORIGIN)
  ).replace(/\/$/, '')
  const serverAddress = get('ServerAddress', FIXED_PUBLIC_SITE_ORIGIN).replace(/\/$/, '')

  const validateGeneral = () => {
    if (!verifyJson(get('TopupGroupRatio'), 'object')) return '充值分组倍率不是合法的 JSON 对象'
    const payMsg = validatePayMethods(get('PayMethods'))
    if (payMsg) return `充值方式：${payMsg}`
    const amtMsg = validateAmountOptions(get('payment_setting.amount_options'))
    if (amtMsg) return `预设充值数量：${amtMsg}`
    const discMsg = validateAmountDiscount(get('payment_setting.amount_discount'))
    if (discMsg) return `充值折扣：${discMsg}`
    return null
  }

  const validateCreem = () => {
    if (!verifyJson(get('CreemProducts'), 'array')) return 'Creem 产品配置不是合法的 JSON 数组'
    return null
  }

  const validateWaffo = () => {
    const msg = validatePayMethods(get('WaffoPayMethods'))
    if (msg) return `Waffo 支付方式：${msg}`
    return null
  }

  return (
    <div className="space-y-4">
      <PaymentComplianceCard options={options} />

      <PaymentChannelCard
        title="支付通用"
        subtitle="ServerAddress 由系统设置维护；下方为回调基址预览与各渠道共享的金额预设"
        status="ready"
        keys={GENERAL_KEYS}
        onSave={save}
        onReload={reload}
        saving={saving}
        validate={validateGeneral}
      >
        <Form layout="vertical">
          <CopyableLine label="对外服务根地址（只读）" value={serverAddress} />
          <CopyableLine label="当前支付回调基址" value={callbackBase} />
          <Alert
            type="info"
            className="mb-4"
            content="修改 CustomCallbackAddress 后，各渠道异步通知 URL 将基于新基址；用户侧充值页需重新打开以刷新金额选项。"
          />
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="自定义回调基址" extra="留空则使用 ServerAddress">
              <Input
                value={get('CustomCallbackAddress')}
                onChange={(v) => patch({ CustomCallbackAddress: v })}
                placeholder="https://tokentcp.com"
              />
            </Form.Item>
            <Form.Item label="充值价格（元/美元）">
              <InputNumber
                style={{ width: '100%' }}
                precision={2}
                value={getNumber('Price', 7.3)}
                onChange={(v) => patch({ Price: v ?? 0 })}
              />
            </Form.Item>
            <Form.Item label="最低充值数量（美元等价）">
              <InputNumber
                style={{ width: '100%' }}
                value={getNumber('MinTopUp', 1)}
                min={0}
                onChange={(v) => patch({ MinTopUp: v ?? 0 })}
              />
            </Form.Item>
          </div>
          <Form.Item label="预设充值数量">
            <AmountOptionsEditor
              value={get('payment_setting.amount_options', '[10,20,50,100]')}
              onChange={(v) => patch({ 'payment_setting.amount_options': v })}
            />
          </Form.Item>
          <Form.Item label="充值金额折扣">
            <AmountDiscountEditor
              value={get('payment_setting.amount_discount', '{}')}
              onChange={(v) => patch({ 'payment_setting.amount_discount': v })}
            />
          </Form.Item>
          <Form.Item label="充值方式">
            <PayMethodsEditor
              value={get('PayMethods', '[]')}
              onChange={(v) => patch({ PayMethods: v })}
            />
          </Form.Item>
          <Collapse bordered={false} className="settings-advanced-collapse">
            <Collapse.Item header="高级：TopupGroupRatio JSON" name="topup-ratio">
              <JsonField
                value={get('TopupGroupRatio', '{"default":1}')}
                onChange={(v) => patch({ TopupGroupRatio: v })}
                rows={4}
                placeholder='{"default":1,"vip":0.9}'
              />
            </Collapse.Item>
          </Collapse>
        </Form>
      </PaymentChannelCard>

      <PaymentChannelCard
        title="易支付（Epay）"
        subtitle="PayAddress + EpayId + EpayKey 配置完整后用户侧可见在线充值"
        status={epayStatus(get)}
        keys={EPAY_KEYS}
        onSave={save}
        onReload={reload}
        saving={saving}
      >
        <Form layout="vertical">
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="易支付网关地址">
              <Input
                value={get('PayAddress')}
                onChange={(v) => patch({ PayAddress: v.replace(/\/$/, '') })}
                placeholder="https://pay.example.com"
              />
            </Form.Item>
            <Form.Item label="商户 ID">
              <Input value={get('EpayId')} onChange={(v) => patch({ EpayId: v })} />
            </Form.Item>
            <Form.Item label="商户密钥">
              <SensitiveInput value={get('EpayKey')} onChange={(v) => patch({ EpayKey: v })} />
            </Form.Item>
          </div>
          <CopyableLine label="异步通知 URL" value={`${callbackBase}/api/user/epay/notify`} />
        </Form>
      </PaymentChannelCard>

      <PaymentChannelCard
        title="Stripe"
        subtitle="需配置 Webhook 事件 checkout.session.completed / checkout.session.expired"
        status={stripeStatus(get)}
        keys={STRIPE_KEYS}
        onSave={save}
        onReload={reload}
        saving={saving}
      >
        <Form layout="vertical">
          <CopyableLine label="Webhook URL" value={`${callbackBase}/api/stripe/webhook`} />
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Form.Item label="API Secret">
              <SensitiveInput
                value={get('StripeApiSecret')}
                onChange={(v) => patch({ StripeApiSecret: v })}
                placeholder="sk_xxx"
              />
            </Form.Item>
            <Form.Item label="Webhook Secret">
              <SensitiveInput
                value={get('StripeWebhookSecret')}
                onChange={(v) => patch({ StripeWebhookSecret: v })}
                placeholder="whsec_xxx"
              />
            </Form.Item>
            <Form.Item label="Price ID">
              <Input
                value={get('StripePriceId')}
                onChange={(v) => patch({ StripePriceId: v })}
                placeholder="price_xxx"
              />
            </Form.Item>
            <Form.Item label="Stripe 单价（元/美元）">
              <InputNumber
                style={{ width: '100%' }}
                precision={2}
                value={getNumber('StripeUnitPrice', 8)}
                onChange={(v) => patch({ StripeUnitPrice: v ?? 0 })}
              />
            </Form.Item>
            <Form.Item label="Stripe 最低充值（美元）">
              <InputNumber
                style={{ width: '100%' }}
                value={getNumber('StripeMinTopUp', 1)}
                min={0}
                onChange={(v) => patch({ StripeMinTopUp: v ?? 0 })}
              />
            </Form.Item>
            <Form.Item label="允许促销码">
              <Switch
                checked={getBool('StripePromotionCodesEnabled')}
                onChange={(v) => patch({ StripePromotionCodesEnabled: v })}
              />
            </Form.Item>
          </div>
        </Form>
      </PaymentChannelCard>

      <PaymentChannelCard
        title="Creem"
        subtitle="CreemProducts 为 JSON 数组，含 name / productId / price / quota"
        status={creemStatus(get)}
        keys={CREEM_KEYS}
        onSave={save}
        onReload={reload}
        saving={saving}
        validate={validateCreem}
      >
        <Form layout="vertical">
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="API Key">
              <SensitiveInput
                value={get('CreemApiKey')}
                onChange={(v) => patch({ CreemApiKey: v })}
              />
            </Form.Item>
            <Form.Item label="Webhook Secret">
              <SensitiveInput
                value={get('CreemWebhookSecret')}
                onChange={(v) => patch({ CreemWebhookSecret: v })}
              />
            </Form.Item>
            <Form.Item label="测试模式">
              <Switch
                checked={getBool('CreemTestMode')}
                onChange={(v) => patch({ CreemTestMode: v })}
              />
            </Form.Item>
          </div>
          <Form.Item label="Creem 产品列表">
            <JsonField
              value={get('CreemProducts', '[]')}
              onChange={(v) => patch({ CreemProducts: v })}
              rows={8}
              placeholder='[{"name":"基础包","productId":"prod_xxx","price":10,"quota":5000000,"currency":"USD"}]'
            />
          </Form.Item>
        </Form>
      </PaymentChannelCard>

      <PaymentChannelCard
        title="Waffo"
        subtitle="全球支付；私钥类字段留空不覆盖已有配置"
        status={waffoStatus(get, getBool)}
        keys={WAFFO_KEYS}
        onSave={save}
        onReload={reload}
        saving={saving}
        validate={validateWaffo}
      >
        <Form layout="vertical">
          <Form.Item label="启用 Waffo">
            <Switch
              checked={getBool('WaffoEnabled')}
              onChange={(v) => patch({ WaffoEnabled: v })}
            />
          </Form.Item>
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="生产 API Key">
              <SensitiveInput
                value={get('WaffoApiKey')}
                onChange={(v) => patch({ WaffoApiKey: v })}
              />
            </Form.Item>
            <Form.Item label="生产私钥">
              <SensitiveTextArea
                value={get('WaffoPrivateKey')}
                onChange={(v) => patch({ WaffoPrivateKey: v })}
              />
            </Form.Item>
            <Form.Item label="生产公钥证书" extra="非敏感字段；可直接编辑 PEM 内容">
              <SensitiveTextArea
                value={get('WaffoPublicCert')}
                onChange={(v) => patch({ WaffoPublicCert: v })}
                showConfiguredHint={false}
              />
            </Form.Item>
            <Form.Item label="Sandbox 模式">
              <Switch
                checked={getBool('WaffoSandbox')}
                onChange={(v) => patch({ WaffoSandbox: v })}
              />
            </Form.Item>
            <Form.Item label="Sandbox API Key">
              <SensitiveInput
                value={get('WaffoSandboxApiKey')}
                onChange={(v) => patch({ WaffoSandboxApiKey: v })}
              />
            </Form.Item>
            <Form.Item label="Sandbox 私钥">
              <SensitiveTextArea
                value={get('WaffoSandboxPrivateKey')}
                onChange={(v) => patch({ WaffoSandboxPrivateKey: v })}
              />
            </Form.Item>
            <Form.Item label="Sandbox 公钥证书">
              <SensitiveTextArea
                value={get('WaffoSandboxPublicCert')}
                onChange={(v) => patch({ WaffoSandboxPublicCert: v })}
                showConfiguredHint={false}
              />
            </Form.Item>
            <Form.Item label="Merchant ID">
              <Input
                value={get('WaffoMerchantId')}
                onChange={(v) => patch({ WaffoMerchantId: v })}
              />
            </Form.Item>
            <Form.Item label="货币">
              <Input
                value={get('WaffoCurrency', 'USD')}
                onChange={(v) => patch({ WaffoCurrency: v })}
              />
            </Form.Item>
            <Form.Item label="单价">
              <InputNumber
                style={{ width: '100%' }}
                precision={2}
                value={getNumber('WaffoUnitPrice', 1)}
                onChange={(v) => patch({ WaffoUnitPrice: v ?? 0 })}
              />
            </Form.Item>
            <Form.Item label="最低充值">
              <InputNumber
                style={{ width: '100%' }}
                value={getNumber('WaffoMinTopUp', 1)}
                min={0}
                onChange={(v) => patch({ WaffoMinTopUp: v ?? 0 })}
              />
            </Form.Item>
            <Form.Item label="Notify URL">
              <Input value={get('WaffoNotifyUrl')} onChange={(v) => patch({ WaffoNotifyUrl: v })} />
            </Form.Item>
            <Form.Item label="Return URL">
              <Input value={get('WaffoReturnUrl')} onChange={(v) => patch({ WaffoReturnUrl: v })} />
            </Form.Item>
            <Form.Item label="Subscription Return URL">
              <Input
                value={get('WaffoSubscriptionReturnUrl')}
                onChange={(v) => patch({ WaffoSubscriptionReturnUrl: v })}
                placeholder={`${serverAddress}/console/subscription`}
              />
            </Form.Item>
          </div>
          <Form.Item label="Waffo 支付方式">
            <PayMethodsEditor
              value={get('WaffoPayMethods', '[]')}
              onChange={(v) => patch({ WaffoPayMethods: v })}
            />
          </Form.Item>
        </Form>
      </PaymentChannelCard>
    </div>
  )
}
